import type { AxiosError } from 'axios';
import { Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Team } from '@/api/generated/models';
import {
  getGetDepartmentQueryKey,
  getListTeamsQueryKey,
  useDeactivateTeam,
  useDeleteTeam,
  useReactivateTeam
} from '@/api/generated/departments-teams/departments-teams';
import { ConfirmActionModal, KebabTrigger } from '@/components/common';
import { successNotificationIcon } from '@/theme/notificationIcons';

const teamErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface TeamActionsMenuProps {
  departmentId: string;
  team: Team;
  onEdit: () => void;
}

/** Uredi/Obriši, plus Deaktiviraj/Aktiviraj (WP-05) — tim bez ikakve povijesti
 * (nema članova, nikad nije bio dodijeljen na radni paket) se briše kao prije;
 * tim s poviješću se umjesto toga deaktivira ("raspušten"), backend vraća
 * 409 na brisanje u tom slučaju (ista poruka s API-ja, isti obrazac kao Unit/
 * DepartmentActionsMenu). */
export const TeamActionsMenu = ({ departmentId, team, onEdit }: TeamActionsMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);

  const invalidateTeamQueries = () => {
    queryClient.invalidateQueries({ queryKey: getListTeamsQueryKey(departmentId) });
    queryClient.invalidateQueries({ queryKey: getGetDepartmentQueryKey(departmentId) });
  };

  const deleteMutation = useDeleteTeam({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateTeamQueries();
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('departments.team.deleteSuccess')
        });
        setDeleteModalOpen(false);
      },
      onError: (error) => {
        notifications.show({ color: 'red', message: teamErrorMessage(error, t('common.error')) });
      }
    }
  });

  const deactivateMutation = useDeactivateTeam({
    mutation: {
      onSuccess: () => {
        invalidateTeamQueries();
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('departments.team.deactivateSuccess')
        });
        setDeactivateModalOpen(false);
      }
    }
  });

  const reactivateMutation = useReactivateTeam({
    mutation: {
      onSuccess: () => {
        invalidateTeamQueries();
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('departments.team.reactivateSuccess')
        });
      }
    }
  });

  return (
    <>
      <Menu position="bottom-end" withinPortal shadow="md" radius="md" width={180}>
        <Menu.Target>
          <KebabTrigger onClick={(event) => event.stopPropagation()} />
        </Menu.Target>
        <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
          <Menu.Item
            styles={{ item: { color: 'var(--color-text-primary)' } }}
            onClick={() => navigate(`/users/departments/${departmentId}/teams/${team.id}`)}
          >
            {t('departments.detailsLink')}
          </Menu.Item>
          <Menu.Item styles={{ item: { color: 'var(--color-text-primary)' } }} onClick={onEdit}>
            {t('departments.team.editLink')}
          </Menu.Item>
          {team.isActive === false ? (
            <Menu.Item
              styles={{ item: { color: 'var(--color-text-primary)' } }}
              onClick={() =>
                team.id && reactivateMutation.mutate({ id: departmentId, teamId: team.id })
              }
            >
              {t('departments.team.reactivateAction')}
            </Menu.Item>
          ) : (
            <Menu.Item
              styles={{ item: { color: 'var(--color-text-primary)' } }}
              onClick={() => setDeactivateModalOpen(true)}
            >
              {t('departments.team.deactivateAction')}
            </Menu.Item>
          )}
          <Menu.Item
            styles={{ item: { color: 'var(--color-status-danger)' } }}
            onClick={() => setDeleteModalOpen(true)}
          >
            {t('departments.team.deleteAction')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => team.id && deleteMutation.mutate({ id: departmentId, teamId: team.id })}
        loading={deleteMutation.isPending}
        isPaused={deleteMutation.isPaused}
        danger
        title={t('departments.team.deleteTitle')}
        message={t('departments.team.deleteMessage', { name: team.name })}
        confirmLabel={t('departments.team.deleteConfirm')}
      />

      <ConfirmActionModal
        opened={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
        onConfirm={() =>
          team.id && deactivateMutation.mutate({ id: departmentId, teamId: team.id })
        }
        loading={deactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused}
        title={t('departments.team.deactivateTitle')}
        message={t('departments.team.deactivateMessage', { name: team.name })}
        confirmLabel={t('departments.team.deactivateAction')}
      />
    </>
  );
};
